import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Modal, ModalBody, ModalHeader, Text, cn } from '@growtherapy/sprout-ui';

import { preSessionVibesTutorialCompletedAtom, visitorDrawerStateAtom } from '../../state';
import { MusicStateToggleButtonWrapper as MusicStateToggleButton } from '../background-action/MusicStateToggleButton';
import { Keyboard } from './Keyboard';
import { KEY_BLOBS } from './constants';
import { KeyConfiguration, OnKeyPlayedFn, VibesComponentProps } from './types';

const COMMON_ANIMATION_CLASS =
  'transition-opacity ease-in-ease-out invisible duration-3000 opacity-0 group-[.show-all]/keyboard:opacity-100 group-[.show-all]/keyboard:visible group-[:not(.show-all)]/keyboard:h-0';

const LETTER_TO_ANIMATION_CLASS: Record<string, string> = {
  A: `${COMMON_ANIMATION_CLASS} delay-[2s]`,
  D: `${COMMON_ANIMATION_CLASS} delay-[1s]`,
  G: '', // Since the G key is always shown, it doesn't need a class override.
  J: `${COMMON_ANIMATION_CLASS} delay-[1.5s]`,
  L: `${COMMON_ANIMATION_CLASS} delay-[2.5s]`,
};

export function VerticalVibes({
  closePreSessionVibes,
  onKeyPlayed,
  keys,
  bodyClassOverride,
}: VibesComponentProps & {
  onKeyPlayed: OnKeyPlayedFn;
  keys: KeyConfiguration[];
  bodyClassOverride: string;
}) {
  const t = useTranslate();
  return (
    <Modal isOpen onClose={closePreSessionVibes} className='h-full rebrand:bg-surface-inverseEmphasis'>
      <ModalHeader className='[&_button]:rebrand:mobile:text-text-inverse' contentClassName='rebrand:text-text-inverse'>
        {t('telehealth-core:waitingRoom.unwindWhileYouWait')}
      </ModalHeader>
      <ModalBody
        className={cn(
          'flex flex-col justify-center items-center gap-4 h-full w-full overflow-hidden  py-0',
          bodyClassOverride,
        )}
      >
        <div className='flex flex-col justify-center items-center h-full w-full overflow-x-visible overflow-y-visible relative'>
          <Text className='absolute top-1/3 text-center transition-opacity ease-in-ease-out delay-[0s] duration-1000 visible opacity-100 group-[.show-all]/keyboard:opacity-0 group-[.show-all]/keyboard:invisible group-[.show-all]/keyboard:h-0 rebrand:text-text-inverse'>
            {t('telehealth-core:waitingRoom.toMakeMusicTapOnShape')}
          </Text>
          <Keyboard onKeyPlayed={onKeyPlayed} keys={keys} />
        </div>
        <div className='py-2 w-full flex justify-center'>
          <MusicStateToggleButton />
        </div>
      </ModalBody>
    </Modal>
  );
}

export function VerticalVibesWrapper(props: VibesComponentProps) {
  // Tracks whether the user has interacted with the keyboard.
  // Stored in global state so we persist this across navigation,
  // allowing us to skip instructions if they return.
  const [keyPlayed, setKeyPlayed] = useAtom(preSessionVibesTutorialCompletedAtom);
  const [visitorDrawerState, setVisitorDrawerState] = useAtom(visitorDrawerStateAtom);

  useEffect(
    function clearExistingDrawerState() {
      // Close any other open drawers to prevent weird overlap issues
      if (visitorDrawerState) {
        setVisitorDrawerState(null);
      }
    },
    [visitorDrawerState, setVisitorDrawerState],
  );

  const onKeyPlayed = useCallback(() => {
    if (!keyPlayed) {
      setKeyPlayed(true);
    }
  }, [keyPlayed, setKeyPlayed]);

  const keys = useMemo(
    () =>
      KEY_BLOBS.map((key) => {
        return {
          ...key,
          classOverride: LETTER_TO_ANIMATION_CLASS[key.letter],
        };
      }),
    [],
  );
  return (
    <VerticalVibes
      {...props}
      onKeyPlayed={onKeyPlayed}
      keys={keys}
      bodyClassOverride={keyPlayed ? 'group/keyboard show-all gap-0' : 'group/keyboard'}
    />
  );
}
